import { useEffect, useRef, useState } from "react";

const POLL_MS = 5 * 60 * 1000;
const LAST_SEEN_KEY = "app.lastSeenVersion";

export interface Release {
  version: string;
  date: string;
  summary: string;
  /** The customer-facing line (a "Release note:" line in the commit message). null = in the history but not on What's new;
   *  absent = an entry from before the field existed (read as previously curated: feature releases only). */
  note?: string | null;
}

interface VersionFile {
  version: string;
  deployedAt: string | null;
  releases: Release[];
}

export interface VersionCheckResult {
  updateAvailable: boolean;
  justUpdated: boolean;
  currentVersion: string | null;
  releases: Release[];
  dismissJustUpdated: () => void;
}

function compareSemver(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] ?? 0) > (pb[i] ?? 0)) return 1;
    if ((pa[i] ?? 0) < (pb[i] ?? 0)) return -1;
  }
  return 0;
}

async function fetchVersionFile(): Promise<VersionFile | null> {
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`);
    if (!r.ok) return null;
    return await r.json() as VersionFile;
  } catch {
    return null;
  }
}

export function useVersionCheck(): VersionCheckResult {
  const sessionVersionRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [releases, setReleases] = useState<Release[]>([]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    fetchVersionFile().then((data) => {
      if (!data) return;

      const current = data.version;
      sessionVersionRef.current = current;
      setCurrentVersion(current);
      setReleases(data.releases ?? []);

      const lastSeen = localStorage.getItem(LAST_SEEN_KEY) ?? "";
      // Only show "update complete" if they've used the app before and version is newer
      if (lastSeen && compareSemver(current, lastSeen) > 0) {
        setJustUpdated(true);
      }
      localStorage.setItem(LAST_SEEN_KEY, current);

      interval = setInterval(async () => {
        const polled = await fetchVersionFile();
        if (
          polled !== null &&
          sessionVersionRef.current !== null &&
          compareSemver(polled.version, sessionVersionRef.current) > 0
        ) {
          setUpdateAvailable(true);
          clearInterval(interval);
        }
      }, POLL_MS);
    });

    return () => clearInterval(interval);
  }, []);

  return {
    updateAvailable,
    justUpdated,
    currentVersion,
    releases,
    dismissJustUpdated: () => setJustUpdated(false),
  };
}
